/**
 * 「你还有一桌没打完」——跨设备续局的入口卡片。
 *
 * 出现在大厅首屏最上方。用户换了设备、清了缓存、或者从首页重新进来时，
 * 不需要记得房间码也能回到原来的座位（服务端按 accountId 认人，
 * 座位、筹码、手牌都还在）。
 *
 * 视觉上它**置顶但不抢主 CTA**：金色只落在房间码本身（tokens.css 的约定：
 * 金色只准用于房间码 / 筹码 / 当前行动），按钮走描边样式，
 * 整卡不镀金 —— 镀金会把一个次要入口抬成这一屏的主角。
 */
import type { ReactNode } from 'react'

import { IconChips, IconClock } from '../icons/index.js'
import type { ActiveSeat } from '../app/resume.js'
import { useActiveSeats } from '../app/resume.js'
import { useLobbyFlow } from './lobbyFlow.js'
import { groupRoomCode } from './roomCode.js'
import { currentNickname } from './useAuth.js'
import './lobby.css'

export interface ResumeCardProps {
  /** 已连上服务器且人不在牌桌上时才查询 */
  readonly enabled: boolean
}

function describe(s: ActiveSeat): string {
  const parts = [`${s.stack.toLocaleString()} 筹码`]
  if (s.handNo > 0) parts.push(`第 ${s.handNo} 手`)
  if (s.playerCount > 0) parts.push(`${s.playerCount} 人在桌`)
  return parts.join(' · ')
}

export function ResumeCard({ enabled }: ResumeCardProps): ReactNode {
  const seats = useActiveSeats(enabled)
  if (seats.length === 0) return null

  return (
    <section className="resume" aria-label="未打完的牌桌">
      <h2 className="resume__title">
        <IconClock size={16} />
        你还有一桌没打完
      </h2>
      <ul className="resume__list">
        {seats.map((s) => (
          <li key={s.roomId} className="resume__item">
            <div className="resume__meta">
              <span className="resume__code selectable">{groupRoomCode(s.roomCode)}</span>
              {/* 筹码数字走 .num（等宽）：多行并排时小数点/千分位不会左右错位 */}
              <span className="resume__sub num">
                <IconChips size={14} />
                {describe(s)}
              </span>
            </div>
            <button
              type="button"
              className="lobby-btn resume__btn"
              onClick={() => {
                const flow = useLobbyFlow.getState()
                flow.setLastCode(s.roomCode)
                flow.go('join')
                flow.submitJoin(s.roomCode, currentNickname())
              }}
            >
              回到牌桌
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default ResumeCard
